/** @type {import('tailwindcss').Config} */

module.exports = {
	content: [
		'./pages/**/*.{js,ts,jsx,tsx}',
		'./app/**/*.{js,ts,jsx,tsx}',
		'./components/**/*.{js,ts,jsx,tsx}',
		'./strapi/**/*.{js,ts,jsx,tsx}',
	],
	plugins: [require('@tailwindcss/container-queries')],
	theme: {
		extend: {
			aspectRatio: {
				'25/19': '25 / 19',
			},
			colors: {
				// https://www.color-name.com/
				blue: '#2e9aff',
				'blue-ncs': '#0284C7', // ADA gradient blue
				cinnabar: '#E34040', // color in our gradient badges - reddish
				'dark-lavender': '#6F569F', // color in our gradient badges - purple
				'ghost-white': '#f9f9f9', // only used in one place
				'light-silver': '#d9d9d9', // only used in one place, a border, on blog pages. very easy to replace
				persimmon: '#EA580C', // ADA gradient orange
				'raspberry-pink': '#EC4899', // new gradient pink
				'rose-bonbon': '#FB4899', // hvac gradient pink
				'royal-orange': '#FB923C', // gradient orange
				'vivid-cerise': '#DB2777', // ADA gradient pink
				'vivid-cerulean': '#0EA5E9', // gradient blue
				lavender: {
					50: '#F4F3FA',
					100: '#e9e8f5',
					200: '#d4d2ea',
					300: '#bebbe0',
					400: '#9E99D1',
					500: '#7D77C1',
					600: '#6863A1',
					700: '#534f81',
					800: '#3f3c61',
					900: '#353251',
					950: '#2A2840',
				},
			},
			backgroundImage: {
				'gradient-160': 'linear-gradient(160deg, var(--tw-gradient-stops))',
				'gradient-236': 'linear-gradient(236deg, var(--tw-gradient-stops))',

				'hvac-gradient-transparent':
					'linear-gradient(116deg, rgba(251, 146, 60, 0.08) -11.5%, rgba(236, 72, 153, 0.08) 50.25%, rgba(14, 165, 233, 0.08) 111.99%)',
				'dark-nav-style-gradient':
					'linear-gradient(0deg, rgba(0, 0, 0, 0.00) 5%, rgba(0, 0, 0, 0.80) 100%)',
				'hvac-gradient-opaque':
					'linear-gradient(116.4deg, rgba(251, 146, 60, 1) -11.5%, rgba(236, 72, 153, 1) 50.25%, rgba(14, 165, 233, 1) 111.99%)',
				'dark-cta-fade':
					'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(255,255,255,1) 56%)',
			},
			fontFamily: {
				inter: ['var(--font-inter)'],
				'space-grotesk': ['var(--font-space-grotesk)'],
			},
			fontSize: {
				xxs: ['0.563rem', '0.625rem'], // 9px size, 10px line height
				s10: '2.5rem', // 40px
				s12: '3rem', // 48px
				s15: '3.75rem', // 60px
				s24: '6rem', // 96px
			},
			height: {
				5.5: '1.375rem', // 22px
				13: '3.25rem', // 52px
				33: '8.25rem', // 132px
				41: '10.25rem', // 164px
				46: '11.5rem', // 184px
				90: '22.5rem', // 360px
				96: '24rem', // 384px
				200: '50rem', // 800px
			},

			minHeight: {
				68: '17rem', // 272px
			},
			minWidth: {
				39: '9.75rem', // 156px
				156: '39rem', // 624px
			},
			maxWidth: {
				28: '7.375rem', // 118px
				35: '8.75rem', // 140px
				40: '10.75rem', // 172px
				60: '15rem', // 240px
				61: '15.25rem', // 244px
				70: '17.5rem', // 280px
				75: '18.75rem', // 300px
				85: '21.25rem', // 340px
				90: '22.5rem', // 360px
				101: '25.25rem', // 404px
				156: '39rem', // 624px
				1540: '96rem',
			},
			width: {
				5.5: '1.375rem', //22px
				7.5: '1.875rem', // 30px
				70: '17.5rem', // 280px
				75: '18.75rem', // 300px
			},
			spacing: {
				0.75: '0.1875rem', // 3px
				2.5: '0.625rem', // 10px
				4.5: '1.125rem', // 18px
				5.5: '1.375rem', // 22px
				7.5: '1.875rem', //30px
				15: '3.75rem', // 60px
				18: '4.5rem', // 72px
				19: '4.75rem', // 76px
				21: '5.25rem', // 84px
				22: '5.5rem', // 88px
				30: '7.5rem', //120px
				35: '8.75rem', // 140px
				45: '11.25rem', // 180px
				70: '17.5rem', // 280px
				124: '31rem', // 496px
			},
			screens: {
				xs: '420px',
				'2xl': '1440px',
				'3xl': '1664px',
				'-xs': { max: '420px' },
				'-sm': { max: '639px' },
				'-md': { max: '767px' },
				'-lg': { max: '1023px' },
				'-xl': { max: '1279px' },
				'-2xl': { max: '1440px' },
			},
			transitionProperty: {
				'max-height-opacity': 'opacity, max-height',
			},
			lineHeight: {
				tighter: '1.1',
				14: '3.5rem', // 56px
			},
			borderWidth: {
				1.5: '1.5px',
				3: '3px',
			},
			borderOpacity: {
				15: '0.15',
			},
			borderRadius: {
				'5xl': '2rem', // 32px
				'6xl': '2.5rem', // 40px
			},
			boxShadow: {
				'mobile-sticky': '0 -0.25rem 0.75rem 0 rgba(0, 0, 0, 0.10)',
				'infinity-promo': '0px 25px 50px -12px rgba(148, 163, 184, 0.25)',
			},
			padding: {
				0.75: '0.1875rem', // 3px
				13: '3.25rem', // 52px
				22: '5.5rem', // 88px
				23: '5.75rem', // 92px
				30: '7.5rem', // 120px
			},
			duration: {
				250: '250ms',
			},
			margin: {
				22: '5.5rem', // 88px
				25: '6.25rem', // 100px
			},
			zIndex: {
				15: '15',
			},
			keyframes: {
				btnHoverLayer1: {
					'0%': { 'background-color': 'transparent' },
					'25%': { 'background-color': 'rgba(236,72,153,0.4)' },
					'50%': { 'background-color': 'transparent' },
					'75%': { 'background-color': 'rgba(236,72,153,0.4)' },
					'100%': { 'background-color': 'transparent' },
				},
				btnHoverLayer2: {
					'0%': { opacity: '0' },
					'40%': { opacity: '1' },
					'50%': { opacity: '1' },
					'60%': { opacity: '1' },
					'100%': { opacity: '0' },
				},
			},
			animation: {
				btnHoverLayer1: 'btnHoverLayer1 6s infinite',
				btnHoverLayer2: 'btnHoverLayer2 6s infinite',
			},
		},
	},
};
